import React, {useState} from 'react';
import {SIZE, THEME} from 'app/constants';
import {StartTranscriptionButton} from 'app/components/sharedReactComponents/StartStopTranscriptionButton/StartTranscriptionButton';
import {StopTranscriptionButton} from 'app/components/sharedReactComponents/StartStopTranscriptionButton/StopTranscriptionButton';
import {TranscribingStatus} from 'app/components/DeviceDetails/constants';
import {ClassName} from 'app/types/common';
import {LiveScryptDeviceModel} from 'app/components/DeviceDetails/Models/LiveScryptDeviceModel';
import {TranscriptionPopover} from 'app/components/features/transcribing';
import {useCloud} from 'app/components/features/cloud';

interface Props extends ClassName {
  device: LiveScryptDeviceModel;
  size: SIZE;
}

export function StartStopTranscriptionButton({className, device, size}: Props) {
  const id = device.getId();
  const state = device.getTranscribingInfo();
  const {status} = state;
  const disabled = device.isOffline();

  if (status === TranscribingStatus.Started) {
    return (
      <StopTranscriptionButton
        className={className}
        data-id="stop_transcription_button"
        size={size}
        deviceId={id}
        disabled={disabled}
        action={() => device.stopTranscribing()}
      />
    );
  }

  return <StartTranscription className={className} device={device} size={size} />;
}

interface StartTranscriptionProps extends Props {}

function StartTranscription({className, device, size}: StartTranscriptionProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const {user} = useCloud();

  const cap = device.caps.transcribing;

  const id = device.getId();
  const state = device.getTranscribingInfo();
  const {status} = state;
  const disabled = device.isOffline();
  const paused = status === TranscribingStatus.Paused;

  if (!cap) {
    return null;
  }

  const handleStart = async (e: React.MouseEvent<HTMLElement>) => {
    if (!cap.enabled) {
      setAnchorEl(e.currentTarget);
      return;
    }

    if (paused) {
      await device.resumeTranscribing();
      return;
    }

    await device.startTranscribing();
  };

  return (
    <>
      <StartTranscriptionButton
        className={className}
        data-id="start_transcription_button"
        size={size}
        theme={THEME.PRIMARY}
        deviceId={id}
        disabled={disabled}
        action={handleStart}
      >
        {paused ? 'Resume transcription' : 'Start transcription'}
      </StartTranscriptionButton>

      <TranscriptionPopover
        anchorEl={anchorEl}
        capability={cap}
        paymentAccess={user.role.canEditBilling()}
        setAnchorEl={setAnchorEl}
      />
    </>
  );
}
